/**
 * @name useAlunoComProdutos
 * @description Hook personalizado que combina identificação do aluno com carregamento
 * de produtos considerando restrições alimentares usando a vw_cardapio_hoje
 * @author Sistema IS Cantina
 */

import { useCallback } from "react";
import { useProdutoContext } from "../contexts/ProdutoContext";
import { useUserContext } from "../contexts/UserContext";
import { IAlunoTotem } from "../contexts/UserContext/UserContext";

/**
 * Hook que facilita a identificação do aluno e carregamento de produtos com restrições
 */
export const useAlunoComProdutos = () => {
  const { identificarAluno } = useUserContext();
  const { carregarProdutosParaAluno, carregarProdutos, produtos } =
    useProdutoContext();

  /**
   * Identifica o aluno e carrega produtos considerando suas restrições alimentares
   */
  const identificarECarregarProdutos = useCallback(
    async (
      identificador: string,
      tipo: "rfid" | "codigo",
    ): Promise<IAlunoTotem | null> => {
      console.log("🔍 Iniciando identificação com carregamento de produtos:", {
        identificador,
        tipo,
      });

      try {
        // 1. Identificar aluno
        const aluno = await identificarAluno(identificador, tipo);

        if (!aluno) {
          console.log("❌ Aluno não identificado - mantendo produtos gerais");
          // Se não identificou aluno, mantém produtos gerais
          await carregarProdutos();
          return null;
        }

        console.log("✅ Aluno identificado:", aluno.nome);

        // 2. Carregar produtos considerando restrições do aluno
        console.log("🛡️ Carregando produtos com restrições para:", aluno.nome);
        await carregarProdutosParaAluno(aluno.id);

        console.log("✅ Produtos carregados com restrições aplicadas");
        return aluno;
      } catch (error) {
        console.error(
          "❌ Erro no processo de identificação + produtos:",
          error,
        );
        // Em caso de erro, carrega produtos gerais como fallback
        await carregarProdutos();
        return null;
      }
    },
    [identificarAluno, carregarProdutosParaAluno, carregarProdutos],
  );

  /**
   * Volta aos produtos gerais (sem restrições específicas de aluno)
   */
  const carregarProdutosGerais = useCallback(async () => {
    console.log("📦 Carregando produtos gerais (sem restrições específicas)");
    await carregarProdutos();
  }, [carregarProdutos]);

  /**
   * Recarrega produtos para o aluno atual (útil para refresh)
   */
  const recarregarProdutosAluno = useCallback(
    async (alunoId: number) => {
      console.log("🔄 Recarregando produtos para aluno:", alunoId);
      await carregarProdutosParaAluno(alunoId);
    },
    [carregarProdutosParaAluno],
  );

  return {
    identificarECarregarProdutos,
    carregarProdutosGerais,
    recarregarProdutosAluno,
    totalProdutosCarregados: produtos.length,
  };
};

// Exemplo de uso em um componente:
/*
import { useAlunoComProdutos } from '../hooks/useAlunoComProdutos';

const IdentificacaoAluno = () => {
     const { identificarECarregarProdutos, totalProdutosCarregados } = useAlunoComProdutos();

     const handleRFID = async (rfidCode: string) => {
          const aluno = await identificarECarregarProdutos(rfidCode, 'rfid');
          
          if (aluno) {
               console.log(`${totalProdutosCarregados} produtos disponíveis para ${aluno.nome}`);
               // Navegar para tela de produtos
               navigate('/produtos');
          } else {
               console.log('Falha na identificação - mantendo produtos gerais');
          }
     };

     const handleCodigo = async (codigo: string) => {
          const aluno = await identificarECarregarProdutos(codigo, 'codigo');
          
          if (aluno) {
               console.log(`Produtos carregados para ${aluno.nome}`);
               navigate('/produtos');
          }
     };

     return (
          <div>
               <h1>Identificação do Aluno</h1>
               <p>Produtos carregados: {totalProdutosCarregados}</p>
               // ... resto da interface
          </div>
     );
};
*/
